import type { OpticlockProjectSplit } from '@staff-app/shared-types';
import { formatHoursMinutes } from '../lib/duration';
import { ProjectSplitBar } from './ProjectSplitBar';

interface MonthBreakdownProps {
  totalMinutes: number;
  split: OpticlockProjectSplit[];
}

/**
 * The agent's OWN month as a full-width stacked bar; hover it for the per-project split.
 *
 * Delegates the bar AND the tooltip to ProjectSplitBar rather than reimplementing them. An
 * earlier version rendered the breakdown as an always-visible legend and duplicated both — two
 * copies of the same widget is how a colour rule or an ordering rule ends up fixed in one view
 * and not the other. The dashboard and this page now share one implementation, so the tooltip
 * reads identically wherever you meet it.
 *
 * `maxMinutes = totalMinutes`, so the bar is always full width: there is no cross-agent
 * comparison to scale against here, only the split within this one month.
 */
export function MonthBreakdown({ totalMinutes, split }: MonthBreakdownProps) {
  // An empty bar is visual noise that says nothing, and the table already says "nothing logged
  // this month yet".
  if (totalMinutes <= 0 || split.length === 0) return null;

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm"
      data-testid="opticlock-my-breakdown"
    >
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-sm font-semibold">{'Where your month went'}</h2>
        <span
          className="text-sm font-semibold tabular-nums"
          data-testid="opticlock-my-breakdown-total"
        >
          {formatHoursMinutes(totalMinutes)}
        </span>
      </div>

      <ProjectSplitBar
        email="me"
        heading="Where your month went"
        minutes={totalMinutes}
        maxMinutes={totalMinutes}
        byProject={split}
        trackClassName="w-full"
      />
    </div>
  );
}
